import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Edit2 } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { EventDetails } from "@/components/review-event/EventDetails";
import { GuestList } from "@/components/review-event/GuestList";

interface EventDetails {
  title: string;
  description: string;
  date: Date;
  startTime: string;
  endTime: string;
  location: string;
  selectedGuests: string[];
  guestDetails: {
    user_id: string;
    first_name: string;
    last_name: string;
    phone_number: string;
  }[];
}

const ReviewEvent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const eventData = location.state?.eventData as EventDetails;

  if (!eventData) {
    navigate("/create-event");
    return null;
  }

  const handleCreateEvent = async () => {
    setIsCreating(true);
    try {
      // Get current user's ID
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        throw new Error("User not authenticated");
      }

      // First, create the event
      const { data: event, error: eventError } = await supabase
        .from('events')
        .insert({
          name: eventData.title,
          description: eventData.description,
          event_date: format(eventData.date, "yyyy-MM-dd'T'HH:mm:ssXXX"),
          location: eventData.location,
          status: 'planned',
          host_user_id: session.user.id
        })
        .select()
        .single();

      if (eventError) throw eventError;
      if (!event) throw new Error("Failed to create event");

      // Create the attendee records
      if (eventData.selectedGuests && eventData.selectedGuests.length > 0) {
        const attendeeRecords = eventData.selectedGuests.map(guestId => ({
          event_id: event.event_id,
          user_id: guestId,
          rsvp_status: 'invited' as const
        }));

        const { error: attendeeError } = await supabase
          .from('event_attendee')
          .insert(attendeeRecords);

        if (attendeeError) throw attendeeError;
      }

      toast({
        title: "Success",
        description: "Event created successfully",
      });

      // Navigate back to home page
      navigate("/home");
    } catch (error) {
      console.error('Error creating event:', error);
      toast({
        title: "Error",
        description: "Failed to create event. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(-1)}
          className="w-10 h-10"
        >
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h1 className="text-xl font-bold">Review Details</h1>
        <Button 
          variant="ghost" 
          onClick={() => navigate("/create-event", { state: { eventData } })}
        >
          <Edit2 className="w-5 h-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="px-4 pt-20 pb-24">
        <EventDetails
          title={eventData.title}
          description={eventData.description}
          date={eventData.date}
          location={eventData.location}
          startTime={eventData.startTime}
          endTime={eventData.endTime}
        />

        <GuestList
          guests={eventData.guestDetails}
          selectedGuestsCount={eventData.selectedGuests?.length || 0}
          isHost={true}
          hostUserId=""
        />
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
        <Button 
          className="w-full" 
          onClick={handleCreateEvent}
          disabled={isCreating}
        >
          {isCreating ? "Creating Event..." : "Create Event"}
        </Button>
      </div>
    </div>
  );
};

export default ReviewEvent;